import React from "react";
import { useNavigate } from "react-router-dom";
import { T } from "../../app/tokens.js";
import { APP_NAME } from "../../constants/branding.js";
import { DEMO_USER_NAME } from "../../utils/demoIdentity.js";
import { PageHeaderStack } from "../../components/PageHeaderStack.jsx";

const STOPS = [
  {
    key: "home",
    num: "01",
    label: "Home",
    path: "/home",
    question: "What's happening in my portfolio right now?",
    what: "A live view of lease exposure, attendance signals, and the in-focus items that need your attention - the first thing to check each morning.",
    tryThis: "Check the Lease Exposure band - which markets have the most rent rolling off in the next 12 months?",
  },
  {
    key: "offices",
    num: "02",
    label: "Offices",
    path: "/offices",
    question: "Which offices do I have, and how are they performing?",
    what: "The full portfolio list with space-fit, headcount, attendance, and lease status at a glance.",
    tryThis: "Sort by Space Fit and look at any office flagged as Tight or Over - those are the candidates to address first.",
  },
  {
    key: "leases",
    num: "03",
    label: "Leases",
    path: "/leases",
    question: "When do my leases expire, and what decisions are coming up?",
    what: "Every active lease with its timeline, key financial terms, and the renewal / exit decision ahead.",
    tryThis: "Filter to leases in the Decide window over the next 18 months - those need a strategy.",
  },
  {
    key: "reports",
    num: "04",
    label: "Reports",
    path: "/reports",
    question: "How do I share the story with leadership?",
    what: "Ready-to-share reports - headcount, open roles, space fit, and foot traffic - plus the Decisions register of what's been settled.",
    tryThis: "Open Space Fit for a one-page read on where you're tight or loose across the portfolio.",
  },
];

const ANIMATION_CSS = `
  @keyframes sh-ring-draw {
    from { stroke-dashoffset: 251; }
    to   { stroke-dashoffset: 55; }
  }
  @keyframes sh-dot-pulse {
    0%, 100% { opacity: 1; }
    50%       { opacity: 0.15; }
  }
  @keyframes sh-fade-in {
    from { opacity: 0; transform: translateY(7px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  @keyframes sh-pill-glow {
    0%, 100% { box-shadow: 0 0 0 0 rgba(194, 16, 46, 0); }
    50%       { box-shadow: 0 0 0 4px rgba(194, 16, 46, 0.22); }
  }
  @keyframes sh-marker-slide {
    0%   { left: 6%; }
    55%  { left: 60%; }
    75%  { left: 60%; }
    100% { left: 6%; }
  }
  @keyframes sh-chip-blink {
    0%, 48%, 100% { opacity: 1; }
    52%, 96%      { opacity: 0; }
  }
  @keyframes sh-window-pulse {
    0%, 100% { opacity: 0.35; }
    50%       { opacity: 0.7; }
  }
  @keyframes sh-bar-rise {
    from { height: 4px; }
    to   { height: var(--sh-bar-h, 50px); }
  }
  @media (prefers-reduced-motion: reduce) {
    .sh-tour * { animation: none !important; transition: none !important; }
  }
`;

export function StartHerePage({ displayName = null }) {
  const resolvedName = (displayName ?? DEMO_USER_NAME).trim().split(/\s+/)[0]; // DEMO ONLY - falls back to demo identity until Okta cutover
  const navigate = useNavigate();

  return (
    <div style={{
      minHeight: "100%",
      padding: "26px 0",
      background: T.bg,
    }}>
      <style>{ANIMATION_CSS}</style>

      <PageHeaderStack>
      <div
        className="sh-tour"
        data-testid="guided-tour"
        style={{
          background: T.card,
          border: `1px solid ${T.border}`,
          borderRadius: 16,
          overflow: "hidden",
          boxShadow: "0 18px 44px rgba(16,36,58,.12)",
          color: T.tourInk,
          fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
        }}
      >
        <div style={{
          background: `linear-gradient(90deg, ${T.hdrFrom}, ${T.hdrTo})`,
          padding: "22px 26px 20px",
          color: T.white,
        }}>
          <div style={{
            fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: "0.09em",
            textTransform: "uppercase", opacity: 0.82,
          }}>
            Guided tour
          </div>
          <h1 style={{
            fontSize: T.fsXl, fontWeight: T.fwBold,
            margin: "8px 0 6px", lineHeight: 1.25,
          }}>
            Welcome to {APP_NAME}{resolvedName ? `, ${resolvedName}` : ""}
          </h1>
          <p style={{
            fontSize: T.fsBase, lineHeight: 1.55, opacity: 0.94,
            margin: 0, maxWidth: 560,
          }}>
            Four quick stops covering what you'll actually do in the app. Take four minutes now and you'll know exactly where to go.
          </p>
          <div style={{ fontSize: T.fsXs, fontWeight: T.fwBold, opacity: 0.85, marginTop: 12 }}>
            4 stops · about 4 minutes
          </div>
        </div>

        <div data-testid="guided-tour-stops" style={{ padding: "16px 20px 20px" }}>
          {STOPS.map((stop) => (
            <StopCard key={stop.key} stop={stop} />
          ))}
        </div>

        <p style={{
          margin: 0, padding: "0 26px 18px",
          fontSize: T.fsSm, color: T.mid, lineHeight: 1.5,
        }}>
          Spot something off or have an idea? Use the feedback button in the bottom-right on any page.
        </p>

        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "14px 26px", background: T.tourFooter,
          borderTop: `1px solid ${T.border}`, gap: 12,
        }}>
          <button
            type="button"
            data-testid="skip-tour"
            onClick={() => navigate("/home")}
            style={{
              fontSize: T.fsSm, fontWeight: T.fwBold, color: T.mid,
              background: "none", border: 0, cursor: "pointer", padding: "9px 0",
            }}
          >
            Skip the tour
          </button>
          <button
            type="button"
            data-testid="start-with-home"
            onClick={() => navigate("/home")}
            style={{
              background: T.tourBlue, color: T.white, border: 0,
              borderRadius: 9, padding: "9px 18px",
              fontSize: T.fsBase, fontWeight: T.fwBold, cursor: "pointer",
            }}
          >
            Start with Home →
          </button>
        </div>
      </div>
      </PageHeaderStack>
    </div>
  );
}

function StopCard({ stop }) {
  const navigate = useNavigate();
  const Preview = PREVIEW_COMPONENTS[stop.key];

  return (
    <div style={{
      borderBottom: `1px solid ${T.border}`,
      padding: "16px 6px",
      display: "grid",
      gridTemplateColumns: "minmax(0, 1fr) 216px",
      gap: 16,
      alignItems: "start",
    }}>
      {/* Left column */}
      <div>
        <div style={{
          fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase",
          letterSpacing: "0.06em", color: T.light, marginBottom: 8,
        }}>
          <span style={{ color: T.tourBlue }}>{stop.num}</span> · {stop.label}
        </div>

        <div style={{
          fontSize: T.fsMd5, fontWeight: T.fwBold, color: T.tourInk,
          marginBottom: 6, lineHeight: 1.35,
        }}>
          {stop.question}
        </div>

        <div style={{
          fontSize: T.fsSm5, color: T.mid,
          marginBottom: 14, lineHeight: 1.5,
        }}>
          {stop.what}
        </div>

        <div style={{
          fontSize: T.fsSm, color: T.tourInk, lineHeight: 1.5,
          background: T.tourPanel, border: `1px solid ${T.border}`,
          borderRadius: 9, padding: "9px 11px", marginTop: 11,
        }}>
          <span style={{ fontWeight: T.fwBold, color: T.tourBlue }}>Try this: </span>
          {stop.tryThis}
        </div>

        <button
          type="button"
          data-testid={`tour-open-${stop.key}`}
          aria-label={`Open ${stop.label}`}
          onClick={() => navigate(stop.path)}
          style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: T.fsSm, fontWeight: T.fwBold,
            color: T.tourBlue, marginTop: 11,
            padding: "7px 13px", borderRadius: 8,
            border: `1px solid ${T.blueSoftBorder}`,
            background: T.tourTint,
            cursor: "pointer",
          }}
        >
          Open {stop.label} →
        </button>
      </div>

      {/* Right column — CSS-only animated preview */}
      <div style={{
        border: `1px solid ${T.border}`,
        borderRadius: 10,
        background: T.tourPanel,
        padding: "11px",
        minHeight: 124,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        overflow: "hidden",
      }}>
        {Preview && <Preview />}
      </div>
    </div>
  );
}

/* ─── Animated preview sub-components ──────────────────────────────────── */

function HomePreview() {
  const rows = [
    { label: "Next 12 mo", value: "$18.4M", background: "var(--v2-red-tint)", color: "var(--v2-tile-red-fg)" },
    { label: "12–24 mo", value: "$9.1M", background: "var(--v2-amber-tint)", color: "var(--v2-tile-amber-fg)" },
    { label: "Beyond", value: "Secure", background: "var(--v2-green-tint)", color: "var(--v2-tile-green-fg)" },
  ];
  return (
    <div style={{
      display: "flex", flexDirection: "column", gap: 7,
    }}>
      <span style={{ fontSize: T.fsXxxs, fontWeight: T.fwBold, letterSpacing: "0.05em", textTransform: "uppercase", color: T.light }}>
        Lease exposure
      </span>
      {rows.map((row) => (
        <div key={row.label} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: T.fsXxs5 }}>
          <span style={{ color: T.tourInk, fontWeight: T.fwSemibold }}>{row.label}</span>
          <span style={{
            color: row.color, background: row.background,
            borderRadius: 5, padding: "2px 7px", fontWeight: T.fwBold,
            animation: "sh-pill-glow 3s ease-in-out infinite",
          }}>
            {row.value}
          </span>
        </div>
      ))}
    </div>
  );
}

function OfficesPreview() {
  const rows = [
    { name: "NYC Midtown", fit: "Over" },
    { name: "SF HQ",       fit: "Tight" },
    { name: "London",      fit: "OK" },
  ];
  const fitStyle = {
    Over:  { bg: "var(--v2-red-tint)",   color: "var(--v2-tile-red-fg)" },
    Tight: { bg: T.ragAmberBg,           color: "var(--v2-tile-amber-fg)" },
    OK:    { bg: "var(--v2-green-tint)", color: "var(--v2-tile-green-fg)" },
  };
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
      <span style={{ fontSize: T.fsXxxs, fontWeight: T.fwBold, letterSpacing: "0.05em", textTransform: "uppercase", color: T.light }}>
        Space fit
      </span>
      {rows.map((row, i) => {
        const fs = fitStyle[row.fit];
        return (
          <div
            key={row.name}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              padding: "7px 10px", borderRadius: 8,
              border: "1px solid var(--v2-line)", background: "var(--v2-card)",
              animation: `sh-fade-in 0.4s ease-out ${0.25 + i * 0.35}s both`,
            }}
          >
            <span style={{ fontSize: T.fsSm5, color: "var(--v2-dark)", fontWeight: T.fwSemibold }}>
              {row.name}
            </span>
            <span style={{
              fontSize: T.fsXs, fontWeight: T.fwSemibold, padding: "2px 10px", borderRadius: 6,
              background: fs.bg, color: fs.color,
              animation: row.fit === "Over" ? "sh-pill-glow 1.8s ease-in-out 1.3s infinite" : undefined,
            }}>
              {row.fit}
            </span>
          </div>
        );
      })}
    </div>
  );
}

function LeasesPreview() {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <span style={{ fontSize: T.fsXxs, color: "var(--v2-light)" }}>Lease timeline</span>

      {/* Timeline track */}
      <div style={{ position: "relative", height: 28, borderRadius: 8, background: "var(--v2-track)" }}>
        {/* Red decision-window band on the right */}
        <div style={{
          position: "absolute", right: 0, top: 0, bottom: 0, width: "35%",
          borderRadius: "0 8px 8px 0",
          background: "var(--v2-red-tint)",
          borderLeft: "1px solid var(--v2-red-border)",
          animation: "sh-window-pulse 2.2s ease-in-out infinite",
        }} />
        {/* Sliding today marker */}
        <div style={{
          position: "absolute", top: -5, bottom: -5, width: 2,
          background: "var(--v2-accent)", borderRadius: 2,
          animation: "sh-marker-slide 4.5s ease-in-out infinite",
        }} />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <span style={{ fontSize: T.fsXxs, color: "var(--v2-light)" }}>Today</span>
        <span style={{ fontSize: T.fsXxs, color: "var(--v2-red)", fontWeight: T.fwBold }}>Decide window</span>
      </div>

      {/* Blinking chip */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 4 }}>
        <span style={{
          fontSize: T.fsXs, fontWeight: T.fwBold, padding: "3px 10px", borderRadius: 999,
          background: "var(--v2-red-tint)", color: "var(--v2-tile-red-fg)",
          border: "1px solid var(--v2-red-border)",
          animation: "sh-chip-blink 2.6s step-end infinite",
        }}>
          Decide now
        </span>
      </div>
    </div>
  );
}

function ReportsPreview() {
  const bars = [
    { h: "52px", delay: "0s" },
    { h: "72px", delay: "0.15s" },
    { h: "42px", delay: "0.3s" },
    { h: "84px", delay: "0.45s" },
    { h: "62px", delay: "0.6s" },
    { h: "70px", delay: "0.75s" },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", justifyContent: "flex-end", gap: 5 }}>
      <span style={{ fontSize: T.fsXxxs, fontWeight: T.fwBold, letterSpacing: "0.05em", textTransform: "uppercase", color: T.light }}>
        Space Fit report
      </span>
      <div style={{
        display: "flex", alignItems: "flex-end", gap: 7,
        height: 96, paddingBottom: 0,
        borderBottom: "2px solid var(--v2-line)",
      }}>
        {bars.map((b, i) => (
          <div
            key={i}
            style={{
              flex: 1,
              "--sh-bar-h": b.h,
              height: b.h,
              borderRadius: "4px 4px 0 0",
              background: i % 2 === 0 ? "var(--v2-accent)" : "var(--v2-blue-tint)",
              animation: `sh-bar-rise 1.1s ease-out ${b.delay} infinite alternate`,
            }}
          />
        ))}
      </div>
      <div style={{ display: "flex", marginTop: 5 }}>
        {["M", "T", "W", "T", "F", "S"].map((d, i) => (
          <span key={i} style={{ flex: 1, textAlign: "center", fontSize: T.fsXxxs, color: "var(--v2-light)" }}>{d}</span>
        ))}
      </div>
    </div>
  );
}

const PREVIEW_COMPONENTS = {
  home:          HomePreview,
  offices:       OfficesPreview,
  leases:        LeasesPreview,
  reports:       ReportsPreview,
};
